/* Membership journey memory.

   The signup journey used to live entirely in the URL and in the DOM, so
   anything that navigated away destroyed it: signing in, a failed checkout,
   the back button. The member came back to step one and retyped everything,
   including their ID number.

   Storage is deliberately split in two.

   The DURABLE half (localStorage, 24h expiry) holds only the shape of the
   journey - which tier, which appliance, which step. None of it is personal.
   It has to outlive the tab because Shopify's new customer accounts sign a
   member in on a different origin (account.foreverfrozen.co.za) and often
   return them to their account page rather than the page they came from. If
   that lands in a new tab, sessionStorage is already gone.

   The SESSION half (sessionStorage) holds what the member typed: name, ID
   number, income, address. Scoped to the tab and cleared when the tab
   closes, so a captured ID number does not sit on a shared machine. This was
   the original design and the reasoning still holds - it just should never
   have been carrying the tier as well.

   Nothing here is trusted by the server. It is a convenience for the person
   filling the form; the service re-validates every field on submission. */
(function (w) {
  'use strict';

  var DURABLE_KEY = 'ff_journey_route';
  var SESSION_KEY = 'ff_journey';
  var TTL_MS = 24 * 60 * 60 * 1000;

  /* Keys that may persist beyond the tab. Everything else is session-only. */
  var DURABLE = ['tier', 'appliance', 'step', 'variant'];

  function isDurable(key) {
    return DURABLE.indexOf(key) !== -1;
  }

  /* ---- durable half ------------------------------------------------- */

  function readDurable() {
    try {
      var raw = JSON.parse(w.localStorage.getItem(DURABLE_KEY) || '{}') || {};
      if (raw.t && Date.now() - raw.t > TTL_MS) {
        w.localStorage.removeItem(DURABLE_KEY);
        return {};
      }
      return raw.d || {};
    } catch (e) {
      return {};
    }
  }

  function writeDurable(obj) {
    try {
      w.localStorage.setItem(DURABLE_KEY,
        JSON.stringify({ t: Date.now(), d: obj || {} }));
    } catch (e) {
      /* Private browsing, or storage full. Never break the page. */
    }
  }

  /* ---- session half -------------------------------------------------- */

  function readSession() {
    try {
      return JSON.parse(w.sessionStorage.getItem(SESSION_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeSession(obj) {
    try {
      w.sessionStorage.setItem(SESSION_KEY, JSON.stringify(obj || {}));
    } catch (e) {}
  }

  /* ---- combined ------------------------------------------------------ */

  function read() {
    var out = {};
    var d = readDurable();
    var s = readSession();
    var k;
    for (k in d) {
      if (Object.prototype.hasOwnProperty.call(d, k)) out[k] = d[k];
    }
    for (k in s) {
      if (Object.prototype.hasOwnProperty.call(s, k)) out[k] = s[k];
    }
    return out;
  }

  var Journey = {

    get: read,

    /* Merge, don't replace, so each page can contribute what it knows
       without having to carry the rest. Each key lands in the half it
       belongs to. */
    save: function (patch) {
      var d = readDurable();
      var s = readSession();
      var touchedD = false, touchedS = false;

      for (var k in patch) {
        if (!Object.prototype.hasOwnProperty.call(patch, k)) continue;
        if (isDurable(k)) {
          d[k] = patch[k];
          touchedD = true;
        } else {
          s[k] = patch[k];
          touchedS = true;
        }
      }

      if (touchedD) writeDurable(d);
      if (touchedS) writeSession(s);
      return read();
    },

    clear: function () {
      try { w.sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
      try { w.localStorage.removeItem(DURABLE_KEY); } catch (e) {}
    },

    /* Clear only what the member typed, keeping the route. Used after a
       successful application: there is no reason to hold an ID number once
       it has been submitted, but the tier is still useful if they bounce
       out of checkout and come back. */
    clearPersonal: function () {
      try { w.sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
    },

    /* The tier and appliance normally travel in the query string. When a
       member comes back from signing in, Shopify returns them to the path
       without it - and with new customer accounts, often to a different
       page entirely - so fall back to what was stored. */
    param: function (name) {
      var v = new URLSearchParams(w.location.search).get(name);
      if (v) {
        var patch = {};
        patch[name] = v;
        Journey.save(patch);
        return v;
      }
      return read()[name] || '';
    },

    /* Save form fields as they are typed and put them back on return.
       `map` is { storageKey: cssSelector }. */
    bindFields: function (root, map) {
      var saved = read();

      Object.keys(map).forEach(function (key) {
        var el = root.querySelector(map[key]);
        if (!el) return;

        if (saved[key] != null && saved[key] !== '' && !el.value) {
          el.value = saved[key];
        }

        var handler = function () {
          var patch = {};
          patch[key] = el.value;
          Journey.save(patch);
        };
        el.addEventListener('input', handler);
        el.addEventListener('change', handler);
      });
    },

    /* Shopify's login sends the member back to `return_url`, but the theme
       was passing the path only - so the tier fell off and the journey
       restarted. Carry the query string too. */
    fixSignInLinks: function () {
      var here = encodeURIComponent(w.location.pathname + w.location.search);
      var links = document.querySelectorAll('a[href*="return_url="]');
      Array.prototype.forEach.call(links, function (a) {
        a.setAttribute('href', a.getAttribute('href')
          .replace(/return_url=[^&]*/, 'return_url=' + here));
      });
    },

    /* Put a member back where they were.

       New customer accounts frequently ignore return_url and drop the member
       on their Shopify account page. This runs on the membership pages: if
       someone arrives signed in, with a route saved, and no tier in the URL,
       send them on with the tier restored rather than making them start over. */
    resume: function (expects) {
      expects = expects || 'tier';
      var params = new URLSearchParams(w.location.search);
      if (params.get(expects)) return false;      /* nothing to fix */

      var saved = readDurable();
      if (!saved[expects]) return false;          /* nothing to restore */

      params.set(expects, saved[expects]);
      if (saved.appliance && !params.get('appliance')) {
        params.set('appliance', saved.appliance);
      }
      w.location.replace(w.location.pathname + '?' + params.toString());
      return true;
    }
  };

  w.FFJourney = Journey;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', Journey.fixSignInLinks);
  } else {
    Journey.fixSignInLinks();
  }

})(window);
